import { isSameDay } from 'date-fns';
import { getRange } from './dates';
import type { Range, Value } from '@fshr/shared/types';
import { getRTL } from '@micro-apps/env';

/**
 * Returns a value no smaller than min and no larger than max.
 *
 * @param {Date} value Value to return.
 * @param {Date} min Minimum return value.
 * @param {Date} max Maximum return value.
 * @returns {Date} Value between min and max.
 */
export function between<T extends Date>(value: T, min?: T | null, max?: T | null): T {
	if (min && Math.floor(min.getTime() / 1000) > Math.floor(value.getTime() / 1000)) {
		return min;
	}

	if (max && Math.floor(max.getTime() / 1000) < Math.floor(value.getTime() / 1000)) {
		return max;
	}

	return value;
}

function isValueWithinRange(value: Date, range: Range<Date>): boolean {
	return (
		isSameDay(value, range[0]) ||
		isSameDay(value, range[1]) ||
		(Math.floor(range[0].getTime() / 1000) <= Math.floor(value.getTime() / 1000) &&
			Math.floor(range[1].getTime() / 1000) >= Math.floor(value.getTime() / 1000))
	);
}

export function isSelected(
	date: Date,
	args: {
		value?: Value;
	}
): boolean {
	const { value } = args;
	const range = getRangeFromValue({ value: value });
	if (!range) return false;

	if (Array.isArray(range)) {
		return isValueWithinRange(date, range);
	}

	return date.getTime() === range?.getTime();
}

export function isAvailable(value: Date, unavailable: Date[] = []): boolean {
	return !unavailable.some(date => isSameDay(date, value));
}

export function isRestricted(value: Date, restricted: Date[] = []): boolean {
	return restricted.some(date => isSameDay(date, value));
}

export function isRangeWithinRange(greaterRange: Range<Date>, smallerRange: Range<Date>): boolean {
	return (
		Math.floor(greaterRange[0].getTime() / 1000) <= Math.floor(smallerRange[0].getTime() / 1000) &&
		Math.floor(greaterRange[1].getTime() / 1000) >= Math.floor(smallerRange[1].getTime() / 1000)
	);
}

function doRangesOverlap(range1: Range<Date>, range2: Range<Date>): boolean {
	return isValueWithinRange(range1[0], range2) || isValueWithinRange(range1[1], range2);
}

function getRangeClassNames(
	valueRange: Range<Date>,
	dateRange: Range<Date>,
	baseClassName: string,
	skipRangePadding = false,
	nightlyRatesExperiment = false
): string[] {
	const isRange = doRangesOverlap(dateRange, valueRange);
	const isRTL = getRTL();
	const classes: string[] = [];

	// The selection fill (black) is anchored on the child elements (the central 36×36 div
	// (.range-center) and the two range-padding siblings) instead of the button itself. The
	// rounded corners are likewise applied to .range-center, so a single-day selection renders as
	// a perfect circle, while multi-day ranges keep a continuous capsule rounded only at the caps.
	// When the nightly rates experiment is ON the selection must be fully squared,
	// so the range caps drop their rounded corners. The full variant-prefixed token
	// must live inside the literal so Tailwind's content scanner emits its CSS rule.
	const centerRoundedL = nightlyRatesExperiment
		? '[&>div.range-center]:rounded-none'
		: '[&>div.range-center]:rounded-l-full';
	const centerRoundedR = nightlyRatesExperiment
		? '[&>div.range-center]:rounded-none'
		: '[&>div.range-center]:rounded-r-full';
	const centerRoundedFull = nightlyRatesExperiment
		? '[&>div.range-center]:rounded-none'
		: '[&>div.range-center]:rounded-full';

	if (isRange) {
		classes.push(baseClassName);
		if (!skipRangePadding) {
			classes.push(
				'[&>div.range-center]:bg-button-primary [&>.range-padding-left]:bg-button-primary [&>.range-padding-right]:bg-button-primary'
			);
		}

		const isRangeStart = isValueWithinRange(valueRange[0], dateRange);
		const isRangeEnd = isValueWithinRange(valueRange[1], dateRange);

		if (isRangeStart) {
			if (isRTL) {
				classes.push(`${baseClassName}Start ${centerRoundedR} [&>.range-padding-right]:invisible`);
			} else {
				classes.push(`${baseClassName}Start ${centerRoundedL} [&>.range-padding-left]:invisible`);
			}
		}

		if (isRangeEnd) {
			if (isRTL) {
				classes.push(`${baseClassName}End ${centerRoundedL} [&>.range-padding-left]:invisible`);
			} else {
				classes.push(`${baseClassName}End ${centerRoundedR} [&>.range-padding-right]:invisible`);
			}
		}

		if (isRangeStart && isRangeEnd) {
			classes.push(
				`${baseClassName}BothEnds ${centerRoundedFull} [&>.range-padding-left]:invisible [&>.range-padding-right]:invisible`
			);
		}
	}

	return classes;
}

function isCompleteValue<T>(value: T | null | Range<T | null>): value is T | Range<T> {
	if (Array.isArray(value)) {
		return value[0] !== null && value[1] !== null;
	}

	return value !== null;
}

function getRangeFromValue(args: { value?: Value }): Range<Date> | Date | undefined {
	const { value } = args;
	if (!value || !isCompleteValue(value)) {
		return;
	}

	return (() => {
		if (Array.isArray(value)) {
			return value;
		}

		return value;
	})();
}

export function getTileClasses(
	args: {
		date?: Date | Range<Date>;
		value?: Value;
	},
	baseClassName?: string,
	nightlyRatesExperiment = false
): string[] {
	if (!args) {
		throw new Error('args is required');
	}

	const { value, date } = args;

	const className = `${baseClassName ?? 'FSHCalendarView'}__tile`;
	const classes = [className];

	if (!date) {
		return classes;
	}

	const now = new Date();
	const dateRange = (() => {
		if (Array.isArray(date)) {
			return date;
		}

		return getRange(date);
	})();

	if (isValueWithinRange(now, dateRange)) {
		classes.push(`${className}--now`);
	}

	if (!value || !isCompleteValue(value)) {
		return classes;
	}

	const valueRange = (() => {
		if (Array.isArray(value)) {
			return value;
		}

		return getRange(value);
	})();

	if (isRangeWithinRange(valueRange, dateRange)) {
		classes.push(`${className}--active`);
	} else if (doRangesOverlap(valueRange, dateRange)) {
		classes.push(`${className}--hasActive`);
	}

	const valueRangeClassNames = getRangeClassNames(
		valueRange,
		dateRange,
		`${className}--range`,
		false,
		nightlyRatesExperiment
	);

	classes.push(...valueRangeClassNames);

	return classes;
}
